import { Processor } from '.';
import { Node } from '../Node';
import { Collider } from '../component';
import { AbstractMesh } from '@babylonjs/core';
import { BoxDimensions, Report } from '../../interfaces';
import { ComponentInnerState } from './Processor';

interface ColliderInnerState extends ComponentInnerState {
	mesh?: string | BoxDimensions;
	isColliding?: boolean;
	isObserved?: boolean;
	isGenerated?: boolean;
	isDebugging?: boolean;
	reporter?: Report;
}

export class ColliderProcessor extends Processor {
	private checkCollision(
		mesh: AbstractMesh,
		component: Collider,
		node: Node,
		iState: ColliderInnerState
	): void {
		const collisions = this.engine.colissionManager.getCollisions(mesh);

		if (component.state.ignoreConnected) {
			const relatives = node.getAllRelativeNodes();
			collisions.forEach((reporters, m) => {
				reporters.forEach((r) => {
					if (relatives.some((n) => n.id == r.id)) {
						reporters.delete(r);
						if (reporters.size == 0) {
							collisions.delete(m);
						}
					}
				});
			});
		}
		if (collisions.size > 0) {
			iState.isColliding = true;
			this.engine.colissionManager.onCollision.notifyObservers([
				mesh,
				Array.from(collisions.keys()),
			]);
			const all = new Set<Report>();
			collisions.forEach((set) => set.forEach((v) => all.add(v)));
			component.onCollision.forEach((callback) => callback(all));
		} else {
			if (iState.isColliding) {
				iState.isColliding = false;
				this.engine.colissionManager.onCollisionEnd.notifyObservers(mesh);
				component.onCollisionEnd.forEach((callback) => callback());
			}
		}
	}

	public start(component: Collider, node: Node): boolean {
		if (!component.state.mesh) {
			return false;
		}
		const iState: ColliderInnerState = new Object();
		iState.mesh = component.state.mesh;

		iState.reporter = { id: node.id, type: component.name };

		if (typeof component.state.mesh === 'string') {
			iState.meshAssembly = this.engine.meshFactory.getMeshAssembly(component.state.mesh);
			iState.isGenerated = false;
		} else {
			iState.meshAssembly = this.engine.meshFactory.createBox(
				Object.assign({ isVisible: true }, component.state.mesh),
				'collider'
			);
			iState.meshAssembly.visibility = 0;
			iState.isGenerated = true;
		}

		if (iState.isGenerated) {
			node.addChildAssembly(iState.meshAssembly);
		}

		this.setState(component, iState);
		return true;
	}

	public enable(component: Collider, node: Node): boolean {
		const iState = this.getState<ColliderInnerState>(component);
		if (!iState.meshAssembly || !iState.meshAssembly.isReady() || component.isDisabled) {
			return false;
		}

		const { transformNode } = iState.meshAssembly;
		transformNode.getChildMeshes().forEach((mesh) => {
			if (iState.reporter) {
				this.engine.colissionManager.registerCollider(mesh, iState.reporter);
				if (component.state.observe) {
					iState.isObserved = true;
					this.engine.colissionManager.onBeforeRender.set(mesh.uniqueId, () =>
						this.checkCollision(mesh, component, node, iState)
					);
				}
			}
		});

		return true;
	}

	public disable(component: Collider): boolean {
		const iState = this.getState<ColliderInnerState>(component);
		if (iState.meshAssembly) {
			const { transformNode } = iState.meshAssembly;
			transformNode.getChildMeshes().forEach((mesh) => {
				if (iState.reporter) {
					this.engine.colissionManager.unregisterCollider(mesh, iState.reporter);
					this.engine.colissionManager.onBeforeRender.delete(mesh.uniqueId);
				}
			});
			return true;
		}

		return false;
	}

	public onDestroy(component: Collider): boolean {
		const iState = this.getState<ColliderInnerState>(component);
		if (iState.meshAssembly) {
			const { transformNode } = iState.meshAssembly;
			transformNode.getChildMeshes().forEach((mesh) => {
				if (iState.reporter) {
					this.engine.colissionManager.unregisterCollider(mesh, iState.reporter);
					this.engine.colissionManager.onBeforeRender.delete(mesh.uniqueId);
				}
			});
			if (iState.isGenerated) {
				iState.meshAssembly.destroy();
			}
			this.deleteState(component);
			return true;
		}

		return false;
	}

	public updateState(component: Collider, node: Node): boolean {
		const iState = this.getState<ColliderInnerState>(component);
		if (iState.meshAssembly) {
			if (component.state.mesh !== iState.mesh) {
				return false;
			}
			if (iState.isObserved !== component.state.observe) {
				iState.isObserved = component.state.observe;
				const { transformNode } = iState.meshAssembly;
				if (iState.isObserved) {
					transformNode.getChildMeshes().forEach((mesh) => {
						this.engine.colissionManager.onBeforeRender.set(mesh.uniqueId, () =>
							this.checkCollision(mesh, component, node, iState)
						);
					});
				} else {
					transformNode.getChildMeshes().forEach((mesh) => {
						this.engine.colissionManager.onBeforeRender.delete(mesh.uniqueId);
					});
				}
			}

			if (iState.isGenerated) {
				node.addChildAssembly(iState.meshAssembly);
			}

			if (this.debug !== iState.isDebugging) {
				this._debug(component);
			}
			return true;
		}

		return false;
	}

	private _debug(component: Collider): void {
		const iState = this.getState<ColliderInnerState>(component);
		if (iState.meshAssembly) {
			iState.isDebugging = this.debug;
			const { transformNode } = iState.meshAssembly;
			transformNode.getChildMeshes().forEach((mesh) => {
				if (iState.isDebugging) {
					mesh.showBoundingBox = true;
					mesh.visibility = !component.isDisabled ? 0.1 : 0;
				} else {
					mesh.showBoundingBox = false;
					mesh.visibility = 0;
				}
			});
		}
	}
}
